import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { getPrice } from '../../utils/pricing';   // ✅ سعر موحّد شامل الضريبة (מע"מ) — نفس جدول القراءات
import { cupsDiff, getMeterChange } from '../../utils/cups';
import useElementWidth from '../../hooks/useElementWidth';

// ════════════════════════════════════════════════════════════
//  كشف حساب المزارع — نافذة كاملة تُفتح بالضغط على اسم المزارع
//  ✅ كل الحسابات هنا مطابقة تماماً لجدول القراءات وعمود "غير مدفوع":
//     الأكواب × سعر الفترة (شامل الضريبة)، مع تبديلات العداد، وحالة
//     الدفع لكل فترة (paidPeriods)، + المتبقي من إضافات أراضيه، +
//     المتبقي من المشاريع.
//  ✅ للعرض فقط — لا يغيّر أي بيانات.
// ════════════════════════════════════════════════════════════

const C = {
  green: '#16a34a', greenBg: '#dcfce7', red: '#dc2626', redBg: '#fee2e2',
  orange: '#ea580c', orangeBg: '#ffedd5', amber: '#b45309', amberBg: '#fef3c7',
  grey: '#6b7280', greyBg: '#f3f4f6', line: '#e5e7eb', primary: 'var(--primary)',
};

const money = n => `₪${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = n => Math.round(Number(n) || 0).toLocaleString();
const num = v => (v === null || v === undefined || v === '' || isNaN(parseFloat(v)) ? null : parseFloat(v));

// ── رسم الأكواب لكل فترة (مدفوع / غير مدفوع) ─────────────────
function CupsChart({ data, ar }) {
  const [ref, width] = useElementWidth();
  const paidKey = ar ? 'أكواب مدفوعة' : 'קוב ששולם';
  const unpaidKey = ar ? 'أكواب غير مدفوعة' : 'קוב שלא שולם';
  const rows = data.map(d => ({ label: d.label, [paidKey]: Math.round(d.paidCups), [unpaidKey]: Math.round(d.unpaidCups) }));
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && (
        <BarChart width={width} height={240} data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
          <XAxis dataKey="label" tick={{ fontFamily: 'Tajawal,Heebo', fontSize: 11 }} />
          <YAxis tick={{ fontFamily: 'Tajawal,Heebo', fontSize: 11 }} />
          <Tooltip formatter={v => v.toLocaleString()} contentStyle={{ fontFamily: 'Tajawal,Heebo' }} />
          <Legend wrapperStyle={{ fontFamily: 'Tajawal,Heebo', fontSize: 12 }} />
          <Bar dataKey={paidKey} stackId="c" fill={C.green} />
          <Bar dataKey={unpaidKey} stackId="c" fill={C.red} radius={[4, 4, 0, 0]} />
        </BarChart>
      )}
    </div>
  );
}

// ── رسم المبالغ لكل فترة (مدفوع / غير مدفوع) ─────────────────
function AmountChart({ data, ar }) {
  const [ref, width] = useElementWidth();
  const paidKey = ar ? 'مدفوع' : 'שולם';
  const unpaidKey = ar ? 'غير مدفوع' : 'לא שולם';
  const rows = data.map(d => ({ label: d.label, [paidKey]: Math.round(d.paidAmount), [unpaidKey]: Math.round(d.unpaidAmount) }));
  return (
    <div ref={ref} style={{ width: '100%' }}>
      {width > 0 && (
        <BarChart width={width} height={240} data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.line} />
          <XAxis dataKey="label" tick={{ fontFamily: 'Tajawal,Heebo', fontSize: 11 }} />
          <YAxis tick={{ fontFamily: 'Tajawal,Heebo', fontSize: 11 }} />
          <Tooltip formatter={v => `₪${v.toLocaleString()}`} contentStyle={{ fontFamily: 'Tajawal,Heebo' }} />
          <Legend wrapperStyle={{ fontFamily: 'Tajawal,Heebo', fontSize: 12 }} />
          <Bar dataKey={paidKey} stackId="a" fill="#0ea5e9" />
          <Bar dataKey={unpaidKey} stackId="a" fill={C.orange} radius={[4, 4, 0, 0]} />
        </BarChart>
      )}
    </div>
  );
}

const Kpi = ({ label, value, color, bg, big }) => (
  <div style={{ background: bg, borderRadius: 12, padding: big ? '14px 16px' : '12px 14px', border: `1.5px solid ${color}33`, minWidth: 0 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color, opacity: 0.85 }}>{label}</div>
    <div style={{ fontSize: big ? 26 : 20, fontWeight: 900, color, marginTop: 2, whiteSpace: 'nowrap', direction: 'ltr', textAlign: 'right' }}>{value}</div>
  </div>
);

const Section = ({ title, children, right }) => (
  <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 14, padding: 14, marginTop: 14 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <div style={{ fontWeight: 900, fontSize: 15, color: 'var(--primary)' }}>{title}</div>
      {right}
    </div>
    {children}
  </div>
);

// ✅ نمط موحّد لأزرار رأس النافذة (نفس الارتفاع والحواف)
const hdrBtn = { height: 38, boxSizing: 'border-box', borderRadius: 8, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', flex: 'none' };

const th = { padding: '8px 10px', fontSize: 12, fontWeight: 800, color: '#374151', background: '#f9fafb', borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', textAlign: 'center' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: `1px solid ${C.line}`, textAlign: 'center', whiteSpace: 'nowrap' };

// ── خلاصة الحساب: جدول على الشاشات العريضة، وأسطر متراصّة على الهاتف (حتى يظهر المتبقي دون تمرير) ──
function SummaryBlock({ rows, grand, ar }) {
  const [ref, width] = useElementWidth();
  const L = { item: ar ? 'البند' : 'סעיף', total: ar ? 'المطلوب' : 'נדרש', paid: ar ? 'المدفوع' : 'שולם', rem: ar ? 'المتبقي' : 'נותר', sum: ar ? 'المجموع' : 'סה"כ' };
  const remColor = v => (v > 0.005 ? C.red : C.green);
  const narrow = width > 0 && width < 560;
  if (narrow) {
    const Line = ({ r, strong }) => (
      <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.line}`, background: strong ? (r.remaining > 0.005 ? C.redBg : C.greenBg) : 'transparent', borderRadius: strong ? 10 : 0, paddingInline: strong ? 10 : 0 }}>
        <div style={{ fontWeight: strong ? 900 : 700, fontSize: strong ? 15 : 14, marginBottom: 6 }}>{r.label}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, fontSize: 12 }}>
          <div><div style={{ color: C.grey }}>{L.total}</div><b style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{money(r.total)}</b></div>
          <div><div style={{ color: C.grey }}>{L.paid}</div><b style={{ color: C.green, direction: 'ltr', unicodeBidi: 'isolate' }}>{money(r.paid)}</b></div>
          <div><div style={{ color: C.grey }}>{L.rem}</div><b style={{ color: remColor(r.remaining), fontSize: strong ? 15 : 13, direction: 'ltr', unicodeBidi: 'isolate' }}>{money(r.remaining)}</b></div>
        </div>
      </div>
    );
    return (
      <div ref={ref}>
        {rows.map((r, k) => <Line key={k} r={r} />)}
        <div style={{ marginTop: 8 }}><Line r={{ ...grand, label: L.sum }} strong /></div>
      </div>
    );
  }
  return (
    <div ref={ref} style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={{ ...th, textAlign: 'right' }}>{L.item}</th><th style={th}>{L.total}</th><th style={th}>{L.paid}</th><th style={th}>{L.rem}</th>
        </tr></thead>
        <tbody>
          {rows.map((r, k) => (
            <tr key={k}>
              <td style={{ ...td, textAlign: 'right' }}>{r.label}</td>
              <td style={td}>{money(r.total)}</td>
              <td style={{ ...td, color: C.green }}>{money(r.paid)}</td>
              <td style={{ ...td, color: C.red, fontWeight: 800 }}>{money(r.remaining)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: grand.remaining > 0.005 ? C.redBg : C.greenBg }}>
            <td style={{ ...td, textAlign: 'right', fontWeight: 900, fontSize: 15 }}>{L.sum}</td>
            <td style={{ ...td, fontWeight: 900 }}>{money(grand.total)}</td>
            <td style={{ ...td, fontWeight: 900, color: C.green }}>{money(grand.paid)}</td>
            <td style={{ ...td, fontWeight: 900, fontSize: 16, color: remColor(grand.remaining) }}>{money(grand.remaining)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// onOpenReadings(farmerId) — اختياري: ينقل لصفحة القراءات مفلترة على هذا المزارع
export default function FarmerAccountModal({ farmer, readings, lands, landExtras, projects, prices, regions, lang, onClose, onOpenReadings }) {
  const ar = lang === 'ar';
  const [year, setYear] = useState('');
  // ✅ قياس عرض الرأس: على الشاشات الضيقة تظهر الأزرار أيقونات فقط حتى تبقى بصف واحد متناسق
  const [hdrRef, hdrW] = useElementWidth();
  const compact = hdrW > 0 && hdrW < 640;

  // إغلاق بزر Escape + منع تمرير الصفحة خلف النافذة
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const fid = String(farmer.id);
  const fullName = ar
    ? (`${farmer.lastName || ''} ${farmer.firstName || ''}`.trim() || farmer.name || farmer.nameHeb)
    : (farmer.nameHeb || farmer.name);

  const landById = useMemo(() => new Map((lands || []).map(l => [String(l.id), l])), [lands]);
  const regionName = id => {
    const r = (regions || []).find(x => String(x.id) === String(id));
    return r ? ((r.nameHeb && r.nameHeb !== r.name) ? r.nameHeb : (r.name || '')) : '';
  };

  // ── كل قراءات هذا المزارع (نفس فلتر عمود "غير مدفوع") ──
  const farmerReadings = useMemo(
    () => (readings || []).filter(r => String(r.farmerId).trim() === fid)
      .sort((a, b) => (a.year - b.year) || String(landById.get(String(a.landId))?.stationNumber || '').localeCompare(String(landById.get(String(b.landId))?.stationNumber || ''))),
    [readings, fid, landById]
  );
  const years = [...new Set(farmerReadings.map(r => r.year))].sort((a, b) => b - a);

  // ── صفوف الفترات (لكل قراءة/فترة) ──
  const periodRows = useMemo(() => {
    const rows = [];
    farmerReadings.forEach(r => {
      if (year && r.year !== Number(year)) return;
      const vals = r.readings || [];
      const pp = r.paidPeriods || [];
      const ip = r.invoicedPeriods || [];
      const mcs = r.meterChanges || [];
      const land = landById.get(String(r.landId));
      for (let i = 0; i < Math.max(0, vals.length - 1); i++) {
        const prev = num(vals[i]);
        const cur = num(vals[i + 1]);
        if (prev === null && cur === null) continue;
        const raw = cupsDiff(vals, i, mcs);           // null = لم تُؤخذ بعد
        const cups = raw && raw > 0 ? raw : 0;
        const price = getPrice(prices, r.year, r.landId, i + 1);
        const amount = cups * price;
        const paid = !!pp[i];
        // ✅ فترة مبلغها صفر (لا استهلاك) لا تحتاج دفعاً — لا تُعرض "غير مدفوع"
        // ✅ قراءة أقل من السابقة (raw < 0) = خطأ إدخال غالباً — تُعرض تحذيراً بدل "لا يوجد مبلغ للدفع"
        const status = raw === null ? 'pending' : paid ? 'paid' : raw < 0 ? 'negative' : amount < 0.005 ? 'nothing' : ip[i] ? 'invoiced' : 'unpaid';
        rows.push({
          key: `${r.id}-${i}`, readingId: r.id, year: r.year, period: i + 1,
          station: land?.stationNumber || r.stationNumber || '—',
          region: land?.regionId ? regionName(land.regionId) : '',
          prev, cur, raw, cups, price, amount, paid, status,
          meterChange: getMeterChange(mcs, i),
        });
      }
    });
    return rows;
  }, [farmerReadings, year, prices, landById]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── الإضافات: لكل أرض يملكها المزارع (مرة واحدة لكل أرض — نفس عمود "غير مدفوع") ──
  const farmerLandIds = (lands || []).filter(l => String(l.farmerId) === fid).map(l => String(l.id));
  const extrasRows = (landExtras || [])
    .filter(e => farmerLandIds.includes(String(e.landId)))
    .map(e => {
      const amount = parseFloat(e.amount) || 0;
      const paid = parseFloat(e.paid) || 0;
      return { id: e.id, station: landById.get(String(e.landId))?.stationNumber || '—', note: e.note || '—', amount, paid, remaining: amount - paid, penalty: e.kind === 'penalty', createdAt: e.createdAt };
    })
    .filter(e => e.amount > 0);

  // ── المشاريع: عضوية هذا المزارع (نفس منطق عمود "غير مدفوع") ──
  const projectRows = [];
  (projects || []).forEach(p => {
    (p.members || []).forEach(m => {
      const mid = m.farmerId && (m.farmerId._id || m.farmerId.id || m.farmerId);
      if (!mid || String(mid) !== fid) return;
      const paid = (m.payments || []).reduce((s, pay) => s + (parseFloat(pay.amount) || 0), 0);
      const required = (m.amount === null || m.amount === undefined) ? null : parseFloat(m.amount) || 0;
      projectRows.push({ id: `${p.id}-${m._id || m.id || ''}`, name: p.name, required, paid, remaining: required === null ? 0 : required - paid, status: p.status });
    });
  });

  // ── المجاميع ──
  const valid = periodRows.filter(r => r.status !== 'pending');
  const paidCups = valid.filter(r => r.paid).reduce((s, r) => s + r.cups, 0);
  const unpaidCups = valid.filter(r => !r.paid).reduce((s, r) => s + r.cups, 0);
  const waterTotal = valid.reduce((s, r) => s + r.amount, 0);
  const waterPaid = valid.filter(r => r.paid).reduce((s, r) => s + r.amount, 0);
  const waterRemaining = waterTotal - waterPaid;
  const extrasTotal = extrasRows.reduce((s, e) => s + e.amount, 0);
  const extrasPaid = extrasRows.reduce((s, e) => s + e.paid, 0);
  const extrasRemaining = extrasRows.reduce((s, e) => s + e.remaining, 0);
  const projTotal = projectRows.reduce((s, p) => s + (p.required || 0), 0);
  const projPaid = projectRows.reduce((s, p) => s + p.paid, 0);
  const projRemaining = projectRows.reduce((s, p) => s + Math.max(0, p.remaining), 0);
  // الإضافات والمشاريع ليست مرتبطة بسنة — تُعرض كاملة دائماً
  const grandTotal = waterTotal + extrasTotal + projTotal;
  const grandPaid = waterPaid + extrasPaid + projPaid;
  const grandRemaining = waterRemaining + extrasRemaining + projRemaining;

  // ── بيانات الرسوم: لكل فترة ──
  const chartData = useMemo(() => {
    const map = new Map();
    valid.forEach(r => {
      const label = `${String(r.year).slice(2)}/${ar ? 'ف' : 'ת'}${r.period}`;
      if (!map.has(label)) map.set(label, { label, order: r.year * 100 + r.period, paidCups: 0, unpaidCups: 0, paidAmount: 0, unpaidAmount: 0 });
      const d = map.get(label);
      if (r.paid) { d.paidCups += r.cups; d.paidAmount += r.amount; } else { d.unpaidCups += r.cups; d.unpaidAmount += r.amount; }
    });
    return [...map.values()].sort((a, b) => a.order - b.order);
  }, [valid, ar]); // eslint-disable-line react-hooks/exhaustive-deps

  const STATUS = {
    paid:     { label: ar ? '✓ مدفوع' : '✓ שולם', color: C.green, bg: C.greenBg },
    invoiced: { label: ar ? '✕ غير مدفوع + 🧾 مفوتر' : '✕ לא שולם + 🧾 חשבונית', color: C.orange, bg: C.orangeBg },
    nothing:  { label: ar ? 'لا يوجد مبلغ للدفع' : 'אין סכום לתשלום', color: C.grey, bg: C.greyBg },
    unpaid:   { label: ar ? '✕ غير مدفوع' : '✕ לא שולם', color: C.red, bg: C.redBg },
    pending:  { label: ar ? 'لم تُؤخذ بعد' : 'טרם נלקחה', color: C.grey, bg: C.greyBg },
    negative: { label: ar ? '⚠️ قراءة أقل من السابقة' : '⚠️ קריאה נמוכה מהקודמת', color: C.orange, bg: C.orangeBg },
  };

  const stations = [...new Set(farmerLandIds.map(id => landById.get(id)?.stationNumber).filter(Boolean))];
  const today = new Date().toLocaleDateString(ar ? 'ar-EG-u-nu-latn' : 'he-IL');

  // ── طباعة كشف الحساب كما يظهر على الشاشة (بالعرض، بخط أصغر، مع الرسوم والألوان) ──
  const handlePrint = () => window.print();

  return createPortal(
    <div className="fam-acc-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(6px,2vw,20px)' }}>
      {/* ✅ عند الطباعة: تُخفى بقية الصفحة وتُطبع النافذة كاملة كما هي على الشاشة */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 7mm; }
          html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
          body > *:not(.fam-acc-overlay) { display: none !important; }
          .fam-acc-overlay { position: static !important; inset: auto !important; background: none !important; padding: 0 !important; display: block !important; }
          .fam-acc-dialog { width: 100% !important; max-width: none !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; border-radius: 0 !important; zoom: 0.85; }
          .fam-acc-header { position: static !important; border-radius: 0 !important; }
          .fam-acc-controls { display: none !important; }
          .fam-acc-dialog .recharts-tooltip-wrapper, .fam-acc-dialog .recharts-tooltip-cursor { display: none !important; }
          .fam-acc-dialog, .fam-acc-dialog * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .fam-acc-dialog tr, .fam-acc-dialog svg { break-inside: avoid; }
          /* ✅ إلغاء قواعد طباعة الجداول العامة داخل الكشف (كانت تخفي العمود الأخير: الحالة/المتبقي، وتغيّر ألوان الرأس والصفوف) */
          .fam-acc-dialog table th:last-child, .fam-acc-dialog table td:last-child { display: table-cell !important; }
          .fam-acc-dialog table thead th { background: #f9fafb !important; color: #374151 !important; }
          .fam-acc-dialog table tr:nth-child(even) td { background: transparent !important; }
          .fam-acc-dialog table th, .fam-acc-dialog table td { border: none !important; border-bottom: 1px solid #e5e7eb !important; padding: 5px 7px !important; }
        }
      `}</style>
      <div className="fam-acc-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={fullName}
        style={{ background: '#f8faf9', borderRadius: 18, width: '100%', maxWidth: 1150, maxHeight: '94vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', direction: 'rtl' }}>

        {/* ── الرأس ── */}
        <div ref={hdrRef} className="fam-acc-header" style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--primary-dark, #14532d)', color: '#fff', padding: '14px 18px', borderRadius: '18px 18px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0, flex: '1 1 200px' }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>{ar ? 'كشف حساب المزارع' : 'דף חשבון חקלאי'} · {today}</div>
            <div style={{ fontSize: 22, fontWeight: 900, fontFamily: 'Heebo,Tajawal,sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullName}</div>
          </div>
          {/* ✅ كل أدوات الرأس بصف واحد وبنفس الارتفاع */}
          <div className="fam-acc-controls" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', flex: '0 0 auto', marginInlineStart: 'auto' }}>
            <select value={year} onChange={e => setYear(e.target.value)} aria-label={ar ? 'السنة' : 'שנה'}
              style={{ ...hdrBtn, width: compact ? 112 : 140, background: '#fff', color: '#111', border: 'none', padding: '0 8px' }}>
              <option value="">{ar ? 'كل السنوات' : 'כל השנים'}</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            {onOpenReadings && (
              <button type="button" onClick={() => { onOpenReadings(farmer.id); onClose(); }}
                title={ar ? 'فتح صفحة القراءات لهذا المزارع' : 'פתח את דף הקריאות של החקלאי'} aria-label={ar ? 'صفحة القراءات' : 'דף הקריאות'}
                style={{ ...hdrBtn, padding: compact ? 0 : '0 12px', width: compact ? 38 : 'auto' }}>
                📏{!compact && <span style={{ marginInlineStart: 6 }}>{ar ? 'القراءات' : 'קריאות'}</span>}
              </button>
            )}
            <button type="button" onClick={handlePrint} title={ar ? 'طباعة' : 'הדפסה'} aria-label={ar ? 'طباعة' : 'הדפסה'}
              style={{ ...hdrBtn, padding: compact ? 0 : '0 12px', width: compact ? 38 : 'auto' }}>
              🖨️{!compact && <span style={{ marginInlineStart: 6 }}>{ar ? 'طباعة' : 'הדפסה'}</span>}
            </button>
            <button type="button" onClick={onClose} title={ar ? 'إغلاق' : 'סגירה'} aria-label={ar ? 'إغلاق' : 'סגירה'}
              style={{ ...hdrBtn, width: 38, padding: 0, fontSize: 18 }}>✕</button>
          </div>
        </div>

        <div id="farmer-account-print" style={{ padding: 'clamp(10px,2vw,18px)' }}>
          {/* ── بيانات المزارع ── */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 13, color: '#374151' }}>
            <span>{ar ? 'رقم الهوية' : 'ת"ז'}: <b>{farmer.idNumber?.startsWith('TMP-') ? '—' : farmer.idNumber}</b></span>
            {farmer.phone && <span>{ar ? 'الهاتف' : 'טלפון'}: <b style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{farmer.phone}</b></span>}
            <span>{ar ? 'المحطات' : 'עמדות'}: <b>{stations.length ? stations.join('، ') : '—'}</b></span>
            <span>{ar ? 'الفترة المعروضة' : 'תקופה מוצגת'}: <b>{year || (ar ? 'كل السنوات' : 'כל השנים')}</b></span>
          </div>

          {/* ── المؤشرات ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 12 }}>
            <Kpi label={ar ? 'إجمالي الأكواب' : 'סה"כ קוב'} value={int(paidCups + unpaidCups)} color="#0369a1" bg="#e0f2fe" />
            <Kpi label={ar ? 'أكواب مدفوعة' : 'קוב ששולם'} value={int(paidCups)} color={C.green} bg={C.greenBg} />
            <Kpi label={ar ? 'أكواب غير مدفوعة' : 'קוב שלא שולם'} value={int(unpaidCups)} color={C.red} bg={C.redBg} />
            <Kpi label={ar ? 'إجمالي الحساب' : 'סה"כ חשבון'} value={money(grandTotal)} color="#374151" bg="#fff" />
            <Kpi label={ar ? 'المدفوع' : 'שולם'} value={money(grandPaid)} color={C.green} bg={C.greenBg} />
            <Kpi label={ar ? 'المتبقي للدفع' : 'נותר לתשלום'} value={money(grandRemaining)} color={grandRemaining > 0.005 ? C.red : C.green} bg={grandRemaining > 0.005 ? C.redBg : C.greenBg} big />
          </div>

          {/* ── الرسوم ── */}
          {chartData.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 }}>
              <Section title={ar ? '🪣 الأكواب لكل فترة' : '🪣 קוב לכל תקופה'}><CupsChart data={chartData} ar={ar} /></Section>
              <Section title={ar ? '💰 المبالغ لكل فترة' : '💰 סכומים לכל תקופה'}><AmountChart data={chartData} ar={ar} /></Section>
            </div>
          )}

          {/* ── جدول القراءات ── */}
          <Section title={ar ? '📏 القراءات والحساب لكل فترة' : '📏 קריאות וחשבון לכל תקופה'}
            right={<span style={{ fontSize: 12, color: C.grey }}>{ar ? 'الأسعار شاملة الضريبة' : 'המחירים כוללים מע"מ'}</span>}>
            {periodRows.length === 0 ? (
              <div style={{ textAlign: 'center', color: C.grey, padding: 16 }}>{ar ? 'لا توجد قراءات' : 'אין קריאות'}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>{ar ? 'المحطة' : 'עמדה'}</th>
                      <th style={th}>{ar ? 'السنة' : 'שנה'}</th>
                      <th style={th}>{ar ? 'الفترة' : 'תקופה'}</th>
                      <th style={th}>{ar ? 'القراءة السابقة' : 'קריאה קודמת'}</th>
                      <th style={th}>{ar ? 'القراءة الحالية' : 'קריאה נוכחית'}</th>
                      <th style={th}>{ar ? 'الأكواب' : 'קוב'}</th>
                      <th style={th}>{ar ? 'سعر الكوب' : 'מחיר לקוב'}</th>
                      <th style={th}>{ar ? 'المبلغ' : 'סכום'}</th>
                      <th style={th}>{ar ? 'الحالة' : 'סטטוס'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodRows.map(r => {
                      const st = STATUS[r.status];
                      return (
                        <tr key={r.key} style={{ background: r.status === 'paid' ? '#f0fdf4' : (r.status === 'pending' || r.status === 'nothing') ? '#fafafa' : (r.status === 'invoiced' || r.status === 'negative') ? '#fff7ed' : '#fff5f5' }}>
                          <td style={{ ...td, fontWeight: 800 }}>{r.station}</td>
                          <td style={td}>{r.year}</td>
                          <td style={td}>{ar ? 'ف' : 'ת'}{r.period}{r.meterChange ? ' 🔄' : ''}</td>
                          <td style={td}>{r.prev === null ? '—' : int(r.prev)}</td>
                          <td style={td}>
                            {r.cur === null ? '—' : int(r.cur)}
                            {r.meterChange && (
                              <div style={{ fontSize: 11, color: '#6d28d9' }}>
                                {ar ? 'تبديل عداد' : 'החלפת מונה'}: {int(r.meterChange.oldFinal)} ← {int(r.meterChange.newInitial)}
                              </div>
                            )}
                          </td>
                          <td style={{ ...td, fontWeight: 800 }}>{r.raw === null ? '—' : int(r.cups)}{r.raw !== null && r.raw < 0 ? ' ⚠️' : ''}</td>
                          <td style={td}>{r.raw === null ? '—' : `₪${r.price.toFixed(2)}`}</td>
                          <td style={{ ...td, fontWeight: 800 }}>{r.raw === null ? '—' : money(r.amount)}</td>
                          <td style={td}><span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: '2px 8px', fontWeight: 800, fontSize: 12 }}>{st.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f9fafb', fontWeight: 900 }}>
                      <td style={{ ...td, textAlign: 'right' }} colSpan={5}>{ar ? 'المجموع' : 'סה"כ'}</td>
                      <td style={{ ...td, fontWeight: 900 }}>{int(paidCups + unpaidCups)}</td>
                      <td style={td}></td>
                      <td style={{ ...td, fontWeight: 900 }}>{money(waterTotal)}</td>
                      <td style={{ ...td, fontSize: 12 }}>
                        <span style={{ color: C.green }}>{money(waterPaid)}</span> / <span style={{ color: C.red }}>{money(waterRemaining)}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Section>

          {/* ── الإضافات ── */}
          {extrasRows.length > 0 && (
            <Section title={ar ? '➕ إضافات الأرض' : '➕ תוספות קרקע'}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>{ar ? 'المحطة' : 'עמדה'}</th>
                    <th style={th}>{ar ? 'السبب' : 'סיבה'}</th>
                    <th style={th}>{ar ? 'المبلغ' : 'סכום'}</th>
                    <th style={th}>{ar ? 'المدفوع' : 'שולם'}</th>
                    <th style={th}>{ar ? 'المتبقي' : 'נותר'}</th>
                  </tr></thead>
                  <tbody>
                    {extrasRows.map(e => (
                      <tr key={e.id} style={{ background: e.remaining > 0.005 ? '#fff5f5' : '#f0fdf4' }}>
                        <td style={{ ...td, fontWeight: 800 }}>{e.station}</td>
                        <td style={{ ...td, whiteSpace: 'normal', textAlign: 'right' }}>{e.penalty ? '⏰ ' : ''}{e.note}</td>
                        <td style={td}>{money(e.amount)}</td>
                        <td style={{ ...td, color: C.green }}>{money(e.paid)}</td>
                        <td style={{ ...td, fontWeight: 800, color: e.remaining > 0.005 ? C.red : C.green }}>{e.remaining > 0.005 ? money(e.remaining) : '✓'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ── المشاريع ── */}
          {projectRows.length > 0 && (
            <Section title={ar ? '🏗️ المشاريع' : '🏗️ פרויקטים'}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>{ar ? 'المشروع' : 'פרויקט'}</th>
                    <th style={th}>{ar ? 'المطلوب' : 'נדרש'}</th>
                    <th style={th}>{ar ? 'المدفوع' : 'שולם'}</th>
                    <th style={th}>{ar ? 'المتبقي' : 'נותר'}</th>
                  </tr></thead>
                  <tbody>
                    {projectRows.map(p => (
                      <tr key={p.id} style={{ background: p.remaining > 0.005 ? '#fff5f5' : '#f0fdf4' }}>
                        <td style={{ ...td, whiteSpace: 'normal', textAlign: 'right', fontWeight: 700 }}>{p.name}</td>
                        <td style={td}>{p.required === null ? (ar ? 'غير محدد بعد' : 'טרם נקבע') : money(p.required)}</td>
                        <td style={{ ...td, color: C.green }}>{money(p.paid)}</td>
                        <td style={{ ...td, fontWeight: 800, color: p.remaining > 0.005 ? C.red : C.green }}>{p.remaining > 0.005 ? money(p.remaining) : '✓'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* ── الخلاصة ── */}
          <Section title={ar ? '🧾 خلاصة الحساب' : '🧾 סיכום חשבון'}>
            <SummaryBlock ar={ar} rows={[
              { label: `💧 ${ar ? 'الماء' : 'מים'} (${int(paidCups + unpaidCups)} ${ar ? 'كوب' : 'קוב'})`, total: waterTotal, paid: waterPaid, remaining: waterRemaining },
              ...(extrasRows.length ? [{ label: `➕ ${ar ? 'الإضافات' : 'תוספות'}`, total: extrasTotal, paid: extrasPaid, remaining: extrasRemaining }] : []),
              ...(projectRows.length ? [{ label: `🏗️ ${ar ? 'المشاريع' : 'פרויקטים'}`, total: projTotal, paid: projPaid, remaining: projRemaining }] : []),
            ]} grand={{ total: grandTotal, paid: grandPaid, remaining: grandRemaining }} />
            {year && (
              <div style={{ fontSize: 12, color: C.grey, marginTop: 8 }}>
                {ar ? 'الماء محسوب للسنة المختارة فقط؛ الإضافات والمشاريع معروضة كاملة لأنها غير مرتبطة بسنة.' : 'המים מחושבים לשנה שנבחרה בלבד; תוספות ופרויקטים מוצגים במלואם.'}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>,
    document.body
  );
}
